// Task groups (docs/12 "Admin web UI"): every user's groups for admins with ViewAllTaskGroups,
// else the user's own; "New task group" opens the wizard (S88) for users who may own groups.
// Filtered by status (chips), source, task type and owner, all in the URL. Task counts come
// from each group's task list.
import { Plus, Stack } from "@phosphor-icons/react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router";
import { Button } from "rizzui";

import { usePermissions } from "../auth/permissions";
import { sectionCrumbs, SECTIONS } from "../layout/nav";
import { groupTasksQuery, taskGroupsQuery } from "../tasks/api";
import { useT } from "../i18n";
import { countByStatus, filterGroups, type GroupFilter } from "../tasks/display";
import { TaskGroupsTable } from "../tasks/list/TaskGroupsTable";
import { refetchInterval, useLiveTasks } from "../tasks/live";
import { LiveBadge } from "../tasks/LiveBadge";
import { useLookups } from "../tasks/lookups";
import { GROUP_STATUSES, SOURCES, type TaskGroup } from "../tasks/model";
import { cardClass } from "../ui/Card";
import { cn } from "../ui/cn";
import { FilterChips } from "../ui/FilterChips";
import { PageHeader } from "../ui/PageHeader";
import { SelectField } from "../ui/SideDrawer";
import { EmptyState, ErrorState, Loading } from "../ui/states";
import { statusLabel } from "../ui/tones";

const capitalise = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

function statusCounts(rows: TaskGroup[]): Record<GroupFilter["status"], number> {
  const c = { all: rows.length, active: 0, paused: 0, archived: 0 };
  for (const g of rows) c[g.status]++;
  return c;
}

function useFilter(): [GroupFilter, (patch: Partial<GroupFilter>) => void] {
  const [params, setParams] = useSearchParams();
  const filter: GroupFilter = {
    status: GROUP_STATUSES.find((s) => s === params.get("status")) ?? "all",
    source: SOURCES.find((s) => s === params.get("source")) ?? "",
    taskType: params.get("type") ?? "",
    owner: params.get("owner") ?? "",
  };
  const set = (patch: Partial<GroupFilter>) => {
    const next = { ...filter, ...patch };
    const query = { status: next.status, source: next.source, type: next.taskType, owner: next.owner };
    setParams(Object.fromEntries(Object.entries(query).filter(([, v]) => v !== "" && v !== "all")), { replace: true });
  };
  return [filter, set];
}

function NewGroupButton() {
  const t = useT().tasks.create;
  return (
    <Link to="/task-groups/new">
      <Button as="span" className="gap-1.5">
        <Plus weight="bold" className="h-4 w-4" aria-hidden /> {t.newGroup}
      </Button>
    </Link>
  );
}

export default function TaskGroupsPage() {
  const { tasks: copy, common } = useT();
  const t = copy.group;
  const crumbs = sectionCrumbs("taskGroups");
  const chips: { key: GroupFilter["status"]; label: string }[] = [
    { key: "all", label: common.states.all },
    ...GROUP_STATUSES.map((s) => ({ key: s, label: capitalise(statusLabel(s)) })),
  ];
  const [filter, setFilter] = useFilter();
  const { can } = usePermissions();
  const everyone = can("ViewAllTaskGroups");
  const canCreate = can("ManageOwnTaskGroups");
  // What the page watches comes from the list it polls when not live: the interval reads the
  // latest answer through a ref.
  const liveRef = useRef(false);
  const poll = () => refetchInterval(liveRef.current);
  const groups = useQuery({ ...taskGroupsQuery(everyone), refetchInterval: poll });
  const lookups = useLookups();
  const all = groups.data ?? [];
  const tasks = useQueries({ queries: all.map((g) => ({ ...groupTasksQuery(g.id), refetchInterval: poll })) });
  const counts = new Map(all.map((g, i) => [g.id, tasks[i]?.data ? countByStatus(tasks[i].data) : undefined]));
  const others = filterGroups(all, { ...filter, status: "all" });
  const rows = filterGroups(all, filter);
  const live = useLiveTasks({ groups: rows.map((g) => g.id) });
  useEffect(() => {
    liveRef.current = live;
  }, [live]);
  const options = (blank: string, list: { id: string; name: string }[]) => [{ value: "", label: blank }, ...list.map((o) => ({ value: o.id, label: o.name }))];

  return (
    <>
      <PageHeader title={SECTIONS.taskGroups.title} breadcrumb={crumbs} description={everyone ? SECTIONS.taskGroups.subtitle : t.mine}>
        <LiveBadge live={live} />
        {canCreate ? <NewGroupButton /> : null}
      </PageHeader>
      <div className="mb-4 flex flex-col gap-3 xl:flex-row xl:items-end xl:justify-between">
        <FilterChips label={t.filterByStatus} chips={chips} value={filter.status} counts={statusCounts(others)} onChange={(status) => setFilter({ status })} />
        <div className="grid gap-3 sm:grid-cols-3">
          <SelectField
            className="xl:w-44"
            label={t.source}
            value={filter.source}
            options={[{ value: "", label: t.allSources }, ...SOURCES.map((s) => ({ value: s, label: copy.source[s] }))]}
            onChange={(v) => setFilter({ source: SOURCES.find((s) => s === v) ?? "" })}
          />
          <SelectField className="xl:w-44" label={copy.columns.taskType} value={filter.taskType} options={options(t.allTaskTypes, lookups.taskTypes)} onChange={(taskType) => setFilter({ taskType })} />
          <SelectField className="xl:w-44" label={copy.columns.owner} value={filter.owner} options={options(t.everyone, lookups.owners)} onChange={(owner) => setFilter({ owner })} />
        </div>
      </div>
      <div className={cn(cardClass, "p-3 lg:p-4")}>
        {groups.isPending ? <Loading /> : null}
        {groups.isError ? <ErrorState title={t.listFailed} message={groups.error.message} /> : null}
        {groups.isSuccess && rows.length === 0 ? (
          <EmptyState
            compact
            icon={<Stack weight="duotone" />}
            title={all.length === 0 ? t.noGroups : t.nothingMatches}
            action={all.length === 0 && canCreate ? <NewGroupButton /> : undefined}
          >
            {all.length === 0 ? t.noGroupsBody : t.nothingMatchesBody}
          </EmptyState>
        ) : null}
        {rows.length > 0 ? <TaskGroupsTable rows={rows} lookups={lookups} counts={(id) => counts.get(id)} /> : null}
      </div>
    </>
  );
}
